import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import './AuthModal.css';

function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

function getPasswordStrength(pw) {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^a-zA-Z0-9]/.test(pw)) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  return Math.min(score, 4);
}

const STRENGTH_LABELS = ['', 'Weak', 'Fair', 'Good', 'Strong'];
const STRENGTH_COLORS = ['', '#ef4444', '#f59e0b', '#3b82f6', '#22c55e'];

export default function AuthModal({ onClose, onSuccess, initialMode = 'login' }) {
  const { login, register, forgotPassword, resetPassword } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => { setMode(initialMode); }, [initialMode]);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const reset  = () => { setError(''); setSuccess(''); };
  const handleDone = () => { onSuccess ? onSuccess() : onClose(); };
  const switchMode = (m) => { setMode(m); reset(); setShowPassword(false); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    reset(); setLoading(true);
    try {
      if (mode === 'login') {
        await login(form.email, form.password);
        handleDone();
      } else if (mode === 'register') {
        await register(form.name, form.email, form.password, form.phone || '');
        handleDone();
      } else if (mode === 'forgot') {
        await forgotPassword(form.email);
        setSuccess('Reset link sent! Check your email.');
      } else if (mode === 'reset') {
        if (form.newPassword !== form.confirmPassword) throw new Error('Passwords do not match');
        if (form.newPassword.length < 8) throw new Error('Min 8 characters');
        await resetPassword(form.newPassword);
        setSuccess('Password updated! Signing you in…');
        setTimeout(() => { setMode('login'); setSuccess(''); }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const googleSignIn = () => {
    const params = new URLSearchParams({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: window.location.origin,
      response_type: 'token',
      scope: 'openid email profile',
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  };

  const pwStrength = getPasswordStrength(form.password);
  const isLogin    = mode === 'login';
  const isRegister = mode === 'register';
  const isForgot   = mode === 'forgot';
  const isReset    = mode === 'reset';
  const isEmailMode = isLogin || isRegister;

  return (
    <div className="auth-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="auth-modal">
        <button className="auth-close" onClick={onClose}>✕</button>
        <div className="auth-logo">📍 Spaczo</div>

        {isEmailMode && <><h2 className="auth-title">{isLogin ? 'Welcome back!' : 'Create account'}</h2><p className="auth-subtitle">{isLogin ? 'Sign in to save favorites and manage listings' : 'Join to list properties and save favorites'}</p></>}
        {isForgot   && <><h2 className="auth-title">Forgot password?</h2><p className="auth-subtitle">We'll send a reset link to your email</p></>}
        {isReset    && <><h2 className="auth-title">Set new password</h2><p className="auth-subtitle">Enter your new password below</p></>}

        {isEmailMode && (
          <>
            <div className="google-signin-wrap">
              <button type="button" className="google-oauth-btn" onClick={googleSignIn} disabled={loading}>
                <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
                  <path d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z" fill="#FBBC05"/>
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
                </svg>
                {isLogin ? 'Sign in with Google' : 'Sign up with Google'}
              </button>
            </div>
            <div className="auth-divider"><span>or continue with</span></div>
            <div className="auth-tabs">
              <button className={isLogin    ? 'active' : ''} onClick={() => switchMode('login')}>Sign In</button>
              <button className={isRegister ? 'active' : ''} onClick={() => switchMode('register')}>Register</button>
            </div>
          </>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          {isRegister && (
            <div className="auth-field">
              <label>Full Name</label>
              <input type="text" placeholder="Rahul Sharma" value={form.name} onChange={e => update('name', e.target.value)} required />
            </div>
          )}

          {(isEmailMode || isForgot) && (
            <div className="auth-field">
              <label>Email Address</label>
              <input type="email" placeholder="you@email.com" value={form.email} onChange={e => update('email', e.target.value)} required />
            </div>
          )}

          {isEmailMode && (
            <div className="auth-field">
              <label>
                Password
                {isLogin && (
                  <button type="button" className="forgot-link" onClick={() => switchMode('forgot')}>Forgot password?</button>
                )}
              </label>
              <div className="auth-pw-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={isRegister ? 'Min 8 characters' : 'Your password'}
                  value={form.password}
                  onChange={e => update('password', e.target.value)}
                  required minLength={8}
                />
                <button type="button" className="auth-eye-btn" onClick={() => setShowPassword(p => !p)} tabIndex={-1}>
                  <EyeIcon open={showPassword} />
                </button>
              </div>
              {isRegister && form.password.length > 0 && (
                <div className="auth-pw-strength">
                  <div className="auth-pw-bar">
                    {[1,2,3,4].map(i => (
                      <div key={i} className="auth-pw-seg" style={{ background: i <= pwStrength ? STRENGTH_COLORS[pwStrength] : 'var(--border)' }} />
                    ))}
                  </div>
                  <span className="auth-pw-label" style={{ color: STRENGTH_COLORS[pwStrength] }}>{STRENGTH_LABELS[pwStrength]}</span>
                </div>
              )}
            </div>
          )}

          {isRegister && (
            <div className="auth-field">
              <label>Phone</label>
              <input type="tel" placeholder="9876543210" value={form.phone} onChange={e => update('phone', e.target.value)} required />
            </div>
          )}

          {isReset && (
            <>
              <div className="auth-field">
                <label>New Password</label>
                <div className="auth-pw-wrap">
                  <input type={showNewPassword ? 'text' : 'password'} placeholder="Min 8 characters" value={form.newPassword} onChange={e => update('newPassword', e.target.value)} required minLength={8} />
                  <button type="button" className="auth-eye-btn" onClick={() => setShowNewPassword(p => !p)} tabIndex={-1}><EyeIcon open={showNewPassword} /></button>
                </div>
              </div>
              <div className="auth-field">
                <label>Confirm Password</label>
                <input type="password" placeholder="Repeat new password" value={form.confirmPassword} onChange={e => update('confirmPassword', e.target.value)} required minLength={8} />
              </div>
            </>
          )}

          {error   && <div className="auth-error">{error}</div>}
          {success && <div className="auth-success">{success}</div>}

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading
              ? <span className="spinner" style={{ width: 18, height: 18, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} />
              : mode === 'login'    ? 'Sign In'
              : mode === 'register' ? 'Create Account'
              : mode === 'forgot'   ? 'Send Reset Link'
              : 'Set New Password'}
          </button>
        </form>

        {isForgot && (
          <div className="auth-footer">Remember your password? <button onClick={() => switchMode('login')}>Sign In</button></div>
        )}
        {isEmailMode && (
          <div className="auth-footer">
            {isLogin ? "Don't have an account? " : 'Already have an account? '}
            <button onClick={() => switchMode(isLogin ? 'register' : 'login')}>
              {isLogin ? 'Register' : 'Sign In'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
